import axios, {
  InternalAxiosRequestConfig,
  AxiosHeaders,
  AxiosError,
} from 'axios';
import { decodeJwt } from 'jose';
import { v4 as uuidv4 } from 'uuid';
import Config from '../config';
import { API_ABORT_TIMEOUT, EMPTY_STRING } from '../constants/constant';
import {
  appInsights,
  getClientInstallationId,
  getSessionId,
} from '../telemetry/appInsights';
import { createLogger, summarizePayload } from '../telemetry/logger';
import {
  APP_INSIGHTS_CLIENT_PLATFORM,
  APP_INSIGHTS_CLIENT_TYPE,
  APP_INSIGHTS_SERVICE_NAMES,
  TELEMETRY_STORAGE_KEYS,
  CORRELATION_FIELDS,
  REQUEST_HEADERS,
  TELEMETRY_PROPERTIES,
  DEPENDENCY_TRACKING,
  STRING_CONSTANTS,
} from '../constants/telemetryConstants';
import { auth } from '../contexts/AuthWebContext';
import { GA_EVENTS } from '../constants/googleAnalyticsEvents';
import { trackGaEvent } from '../utils/googleAnalytics';

interface RequestMetadata {
  startTime: number;
  saasContext: {
    userId: string | null;
    organizationId: string | null;
    featureFlags: string[];
  };
}

interface EnhancedInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  metadata?: RequestMetadata;
}

const apiClient = axios.create({
  baseURL: Config.API_URL,
  timeout: API_ABORT_TIMEOUT,
});

const apiLogger = createLogger('api.client');
const INFO_SLOW_REQUEST_THRESHOLD_MS = 1000;
const WARN_SLOW_REQUEST_THRESHOLD_MS = 3000;

const getCorrelationId = (): string => uuidv4();

const getOperationId = (): string =>
  appInsights.context?.telemetryTrace?.traceID ?? uuidv4();

const getCurrentUserId = (): string | null =>
  globalThis.sessionStorage.getItem(TELEMETRY_STORAGE_KEYS.USER_ID) ?? null;

const getCurrentOrganizationId = (): string | null =>
  globalThis.sessionStorage.getItem(TELEMETRY_STORAGE_KEYS.ORG_ID) ?? null;

const getCurrentEntitlementToken = (): string | null => {
  const token = globalThis.sessionStorage.getItem('entitlementToken') ?? null;
  if (!token) {
    return null;
  }

  try {
    const decoded = decodeJwt(token);
    const exp =
      typeof decoded?.exp === 'number' && Number.isFinite(decoded.exp)
        ? decoded.exp
        : null;

    if (!exp) {
      return null;
    }

    // Do not attach tokens that are expired or near expiry (<= 5 minutes).
    const nowInSeconds = Math.floor(Date.now() / 1000);
    if (exp - nowInSeconds <= 300) {
      return null;
    }

    return token;
  } catch {
    return null;
  }
};

const getActiveFeatureFlags = (): string[] => {
  const flags = localStorage.getItem(TELEMETRY_STORAGE_KEYS.FEATURE_FLAGS);
  return flags ? JSON.parse(flags) : [];
};

const getSaaSContext = () => ({
  userId: getCurrentUserId(),
  organizationId: getCurrentOrganizationId(),
  featureFlags: getActiveFeatureFlags(),
});

const MUTATION_METHODS = ['post', 'put', 'patch', 'delete'] as const;

const isMutationMethod = (method: string | undefined): boolean => {
  if (!method) return false;
  return MUTATION_METHODS.includes(
    method.toLowerCase() as (typeof MUTATION_METHODS)[number],
  );
};

const shouldTrackMutationForUrl = (url: string): boolean => {
  if (!url) return false;
  return !url.includes('/api/v1/analytics/');
};

const normalizeUrlPath = (urlString: string): string => {
  if (!urlString) return EMPTY_STRING;
  const stripped = urlString.split('?')[0];
  const segments = stripped.split('/');

  const normalizedSegments = segments.map((segment) => {
    if (!segment) return segment;

    // Hide high-cardinality path segments.
    if (/^\d+$/.test(segment)) return ':id';
    if (/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(segment)) return ':id';
    if (/^[0-9a-f]{24,}$/i.test(segment)) return ':id';
    if (/^[A-Za-z0-9_-]{24,}$/.test(segment)) return ':id';
    if (segment.includes('%')) return ':id';

    return segment;
  });

  return normalizedSegments.join('/');
};

const getEndpointGroup = (path: string): string => {
  const lowerPath = path.toLowerCase();
  if (
    lowerPath.includes('/service-desk') ||
    lowerPath.includes('/workitem') ||
    lowerPath.includes('/work-item')
  ) {
    return 'service_desk';
  }
  if (lowerPath.includes('/users') || lowerPath.includes('/user')) {
    return 'users';
  }
  if (lowerPath.includes('/assets') || lowerPath.includes('/asset')) {
    return 'assets';
  }
  if (lowerPath.includes('/subscription') || lowerPath.includes('/billing')) {
    return 'billing';
  }
  if (lowerPath.includes('/organization')) {
    return 'organization';
  }
  return 'generic';
};

const getAnalyticsApp = (): 'auth' | 'web' => {
  const hostname = globalThis.location.hostname.toLowerCase();
  if (hostname.startsWith('auth.')) {
    return 'auth';
  }

  return 'web';
};

const getLatencyBucket = (durationMs: number): string => {
  if (durationMs < 200) return 'lt200';
  if (durationMs < 500) return '200_500';
  if (durationMs < 1000) return '500_1000';
  return 'gt1000';
};

const getTelemetryUrl = (
  config: EnhancedInternalAxiosRequestConfig,
): URL | null => {
  const requestUrl = config.url ?? EMPTY_STRING;
  if (!requestUrl) return null;

  const baseUrl =
    config.baseURL && config.baseURL.trim().length > 0
      ? config.baseURL
      : globalThis.location.origin;

  try {
    return new URL(requestUrl, baseUrl);
  } catch {
    return null;
  }
};

const getHeaderValue = (
  headers: InternalAxiosRequestConfig['headers'],
  headerName: string,
): string | undefined => {
  if (!headers) {
    return undefined;
  }

  if (headers instanceof AxiosHeaders) {
    const value = headers.get(headerName);
    return typeof value === 'string' ? value : undefined;
  }

  const value = headers[headerName];
  return typeof value === 'string' ? value : undefined;
};

const summarizeQueryParams = (url: URL | null): string[] => {
  if (!url) {
    return [];
  }

  return Array.from(url.searchParams.keys()).slice(0, 20);
};

const summarizeResponsePayload = (payload: unknown): unknown => {
  if (payload == null) {
    return payload;
  }

  return summarizePayload(payload);
};

export const getClientPlatform = () =>
  navigator.userAgent.includes(STRING_CONSTANTS.MOBILE)
    ? APP_INSIGHTS_CLIENT_PLATFORM.MOBILE_BROWSER
    : APP_INSIGHTS_CLIENT_PLATFORM.DESKTOP_BROWSER;

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const saasContext = getSaaSContext();
  const headers = new AxiosHeaders(config.headers ?? {});

  const clientPlatform = getClientPlatform();

  headers.set(REQUEST_HEADERS.REQUEST_ID, getOperationId());
  if (!headers.has(REQUEST_HEADERS.CORRELATION_ID)) {
    headers.set(REQUEST_HEADERS.CORRELATION_ID, getCorrelationId());
  }

  // Remove request-id header for Zoho endpoints that don't allow it in CORS preflight
  if (
    config.url?.includes('salesiq.zohopublic.com') ||
    config.url?.includes('desk.zoho.com')
  ) {
    headers.delete('request-id');
    headers.delete('Request-Id');
  }

  headers.set(REQUEST_HEADERS.CLIENT_TYPE, APP_INSIGHTS_CLIENT_TYPE.WEB);
  headers.set(REQUEST_HEADERS.CLIENT_PLATFORM, clientPlatform);
  headers.set(REQUEST_HEADERS.CLIENT_VERSION, Config.APP_VERSION);
  headers.set(
    REQUEST_HEADERS.CLIENT_INSTALLATION_ID,
    getClientInstallationId(),
  );
  headers.set(REQUEST_HEADERS.CLIENT_SESSION_ID, getSessionId());
  headers.set(
    REQUEST_HEADERS.CLIENT_ID,
    `${APP_INSIGHTS_CLIENT_TYPE.WEB.toLowerCase()}-${getClientInstallationId()}`,
  );
  headers.set(REQUEST_HEADERS.SERVICE_VERSION, Config.APP_VERSION);
  headers.set(REQUEST_HEADERS.ENVIRONMENT, Config.ENVIRONMENT);
  headers.set(REQUEST_HEADERS.REGION, Config.REGION);
  if (!headers.has(REQUEST_HEADERS.API_VERSION)) {
    headers.set(REQUEST_HEADERS.API_VERSION, '1');
  }
  if (saasContext.userId) {
    headers.set(REQUEST_HEADERS.CLIENT_USER_ID, saasContext.userId);
  }
  if (saasContext.organizationId) {
    headers.set(REQUEST_HEADERS.ORGANIZATION_ID, saasContext.organizationId);
  }
  if (
    !headers.has(REQUEST_HEADERS.ORG_ENTITLEMENT) &&
    getCurrentEntitlementToken()
  ) {
    headers.set(
      REQUEST_HEADERS.ORG_ENTITLEMENT,
      getCurrentEntitlementToken() as string,
    );
  }
  if (saasContext.featureFlags.length > 0) {
    headers.set(
      REQUEST_HEADERS.FEATURE_FLAGS,
      saasContext.featureFlags.join(STRING_CONSTANTS.COMMA),
    );
  }

  const enhanced: EnhancedInternalAxiosRequestConfig = {
    ...config,
    headers,
    metadata: {
      startTime: Date.now(),
      saasContext,
    },
  };

  const url = getTelemetryUrl(enhanced);
  const normalizedPath = normalizeUrlPath(
    url?.pathname ?? enhanced.url ?? EMPTY_STRING,
  );
  apiLogger.debug('request_started', {
    method: enhanced.method?.toUpperCase(),
    url: url?.pathname ?? enhanced.url,
    endpointGroup: getEndpointGroup(normalizedPath),
    correlationId: getHeaderValue(headers, REQUEST_HEADERS.CORRELATION_ID),
    operationId: getHeaderValue(headers, REQUEST_HEADERS.REQUEST_ID),
    orgId: saasContext.organizationId,
    userId: saasContext.userId,
    queryKeys: summarizeQueryParams(url),
    requestBody: summarizePayload(enhanced.data),
  });

  return enhanced;
});

apiClient.interceptors.response.use(
  (response) => {
    const config = response.config as EnhancedInternalAxiosRequestConfig;
    const duration = Date.now() - (config.metadata?.startTime ?? Date.now());
    const target = `${config.url ?? EMPTY_STRING}`;
    const url = getTelemetryUrl(config);
    const params = Object.fromEntries(
      new URLSearchParams(url?.search ?? EMPTY_STRING),
    );

    appInsights.trackDependencyData({
      id: getCorrelationId(),
      target,
      name: `${config.method?.toUpperCase() ?? DEPENDENCY_TRACKING.UNKNOWN_METHOD} ${config.url ?? STRING_CONSTANTS.EMPTY}`,
      duration,
      success: true,
      responseCode: response.status,
      type: DEPENDENCY_TRACKING.TYPE,
      data: config.url,
      properties: {
        [CORRELATION_FIELDS.USER_ID]: getCurrentUserId(),
        [CORRELATION_FIELDS.ORG_ID]: getCurrentOrganizationId(),
        [CORRELATION_FIELDS.SESSION_ID]: getSessionId(),
        [CORRELATION_FIELDS.CLIENT_ID]: `${APP_INSIGHTS_CLIENT_TYPE.WEB.toLowerCase()}-${getClientInstallationId()}`,
        [TELEMETRY_PROPERTIES.CLIENT_TYPE]: APP_INSIGHTS_CLIENT_TYPE.WEB,
        [TELEMETRY_PROPERTIES.CLIENT_VERSION]: Config.APP_VERSION,
        [TELEMETRY_PROPERTIES.CLIENT_PLATFORM]: getClientPlatform(),
        [TELEMETRY_PROPERTIES.SERVICE_NAME]:
          APP_INSIGHTS_SERVICE_NAMES.WEB_CLIENT,
        [TELEMETRY_PROPERTIES.SERVICE_VERSION]: Config.APP_VERSION,
        [TELEMETRY_PROPERTIES.ENVIRONMENT]: Config.ENVIRONMENT,
        [TELEMETRY_PROPERTIES.REGION]: Config.REGION,
        [TELEMETRY_PROPERTIES.CORRELATION_ID]: String(
          config.headers?.[REQUEST_HEADERS.CORRELATION_ID],
        ),
        [TELEMETRY_PROPERTIES.API_VERSION]: String(
          config.headers?.[REQUEST_HEADERS.API_VERSION],
        ),
        [TELEMETRY_PROPERTIES.REQUEST_METHOD]: config.method?.toUpperCase(),
        [TELEMETRY_PROPERTIES.RESPONSE_TIME]: duration.toString(),
        [TELEMETRY_PROPERTIES.SUCCESS]: DEPENDENCY_TRACKING.SUCCESS_VALUE,
        [TELEMETRY_PROPERTIES.USER_AGENT]: navigator.userAgent,
        [TELEMETRY_PROPERTIES.QUERY_PARAMETERS]: params,
      },
    });

    const mutationMethod = config.method?.toLowerCase();
    const normalizedPath = normalizeUrlPath(config.url ?? EMPTY_STRING);
    if (
      isMutationMethod(mutationMethod) &&
      shouldTrackMutationForUrl(normalizedPath)
    ) {
      trackGaEvent(GA_EVENTS.API_MUTATION_SUCCESS, {
        app: getAnalyticsApp(),
        method: mutationMethod,
        endpoint_group: getEndpointGroup(normalizedPath),
        endpoint_path: normalizedPath,
        status_code: response.status,
        latency_bucket_ms: getLatencyBucket(duration),
      });
    }

    let successLogLevel: 'debug' | 'info' | 'warn' = 'debug';
    if (duration >= WARN_SLOW_REQUEST_THRESHOLD_MS) {
      successLogLevel = 'warn';
    } else if (duration >= INFO_SLOW_REQUEST_THRESHOLD_MS) {
      successLogLevel = 'info';
    }

    apiLogger[successLogLevel]('request_completed', {
      method: config.method?.toUpperCase(),
      url: url?.pathname ?? config.url,
      endpointGroup: getEndpointGroup(normalizedPath),
      status: response.status,
      durationMs: duration,
      correlationId: getHeaderValue(
        config.headers,
        REQUEST_HEADERS.CORRELATION_ID,
      ),
      operationId: getHeaderValue(config.headers, REQUEST_HEADERS.REQUEST_ID),
      queryKeys: summarizeQueryParams(url),
      responseBody: summarizeResponsePayload(response.data),
    });

    return response;
  },
  async (error: AxiosError) => {
    const config = error.config as EnhancedInternalAxiosRequestConfig;
    const duration = Date.now() - (config?.metadata?.startTime ?? Date.now());
    const saasContext = config?.metadata?.saasContext;
    const target = `${config?.url ?? EMPTY_STRING}`;
    const url = getTelemetryUrl(
      (config as EnhancedInternalAxiosRequestConfig | undefined) ??
        ({} as EnhancedInternalAxiosRequestConfig),
    );
    const normalizedPath = normalizeUrlPath(
      url?.pathname ?? config?.url ?? EMPTY_STRING,
    );

    appInsights.trackDependencyData({
      id: getCorrelationId(),
      target,
      name: `${config?.method?.toUpperCase() ?? DEPENDENCY_TRACKING.UNKNOWN_METHOD} ${config?.url ?? STRING_CONSTANTS.EMPTY}`,
      duration,
      success: false,
      responseCode: error.response?.status ?? 0,
      type: DEPENDENCY_TRACKING.TYPE,
      data: config?.url,
      properties: {
        [CORRELATION_FIELDS.USER_ID]: getCurrentUserId(),
        [CORRELATION_FIELDS.ORG_ID]: getCurrentOrganizationId(),
        [CORRELATION_FIELDS.SESSION_ID]: getSessionId(),
        [CORRELATION_FIELDS.CLIENT_ID]: `${APP_INSIGHTS_CLIENT_TYPE.WEB.toLowerCase()}-${getClientInstallationId()}`,
        [TELEMETRY_PROPERTIES.CLIENT_TYPE]: APP_INSIGHTS_CLIENT_TYPE.WEB,
        [TELEMETRY_PROPERTIES.CLIENT_VERSION]: Config.APP_VERSION,
        [TELEMETRY_PROPERTIES.CLIENT_PLATFORM]: getClientPlatform(),
        [TELEMETRY_PROPERTIES.SERVICE_NAME]:
          APP_INSIGHTS_SERVICE_NAMES.WEB_CLIENT,
        [TELEMETRY_PROPERTIES.SERVICE_VERSION]: Config.APP_VERSION,
        [TELEMETRY_PROPERTIES.ENVIRONMENT]: Config.ENVIRONMENT,
        [TELEMETRY_PROPERTIES.REGION]: Config.REGION,
        [TELEMETRY_PROPERTIES.CORRELATION_ID]: String(
          config?.headers?.[REQUEST_HEADERS.CORRELATION_ID],
        ),
        [TELEMETRY_PROPERTIES.API_VERSION]: String(
          config?.headers?.[REQUEST_HEADERS.API_VERSION],
        ),
        [TELEMETRY_PROPERTIES.REQUEST_METHOD]: config?.method?.toUpperCase(),
        [TELEMETRY_PROPERTIES.RESPONSE_TIME]: duration.toString(),
        [TELEMETRY_PROPERTIES.SUCCESS]: DEPENDENCY_TRACKING.FAILURE_VALUE,
        [TELEMETRY_PROPERTIES.ERROR_MESSAGE]: error.message,
        [TELEMETRY_PROPERTIES.ERROR_BODY]:
          error.response?.data ?? DEPENDENCY_TRACKING.NO_RESPONSE_DATA,
      },
    });

    appInsights.trackException({
      exception: error,
      properties: {
        [TELEMETRY_PROPERTIES.URL]: config?.url,
        [TELEMETRY_PROPERTIES.METHOD]: config?.method,
        [TELEMETRY_PROPERTIES.STATUS]: error.response?.status?.toString(),
        [CORRELATION_FIELDS.USER_ID]: saasContext?.userId,
        [CORRELATION_FIELDS.ORG_ID]: saasContext?.organizationId,
        [CORRELATION_FIELDS.SESSION_ID]: getSessionId(),
        [TELEMETRY_PROPERTIES.CLIENT_TYPE]: APP_INSIGHTS_CLIENT_TYPE.WEB,
        [TELEMETRY_PROPERTIES.CLIENT_VERSION]: Config.APP_VERSION,
        [TELEMETRY_PROPERTIES.CLIENT_PLATFORM]: getClientPlatform(),
        [TELEMETRY_PROPERTIES.SERVICE_NAME]:
          APP_INSIGHTS_SERVICE_NAMES.WEB_CLIENT,
        [TELEMETRY_PROPERTIES.SERVICE_VERSION]: Config.APP_VERSION,
        [TELEMETRY_PROPERTIES.ENVIRONMENT]: Config.ENVIRONMENT,
        [TELEMETRY_PROPERTIES.REGION]: Config.REGION,
        [TELEMETRY_PROPERTIES.CORRELATION_ID]: String(
          config?.headers?.[REQUEST_HEADERS.CORRELATION_ID],
        ),
        [TELEMETRY_PROPERTIES.ERROR_MESSAGE]:
          error.message ?? DEPENDENCY_TRACKING.UNKNOWN_ERROR,
        [TELEMETRY_PROPERTIES.ERROR_BODY]:
          error.response?.data ?? DEPENDENCY_TRACKING.NO_RESPONSE_DATA,
      },
    });

    const mutationMethod = config?.method?.toLowerCase();
    if (
      isMutationMethod(mutationMethod) &&
      shouldTrackMutationForUrl(normalizedPath)
    ) {
      trackGaEvent(GA_EVENTS.API_MUTATION_FAILURE, {
        app: getAnalyticsApp(),
        method: mutationMethod,
        endpoint_group: getEndpointGroup(normalizedPath),
        endpoint_path: normalizedPath,
        status_code: error.response?.status ?? 0,
        latency_bucket_ms: getLatencyBucket(duration),
      });
    }

    const failureLevel =
      !error.response ||
      (error.response.status >= 500 && error.response.status !== 0)
        ? 'error'
        : 'warn';
    apiLogger[failureLevel](
      'request_failed',
      {
        method: config?.method?.toUpperCase(),
        url: url?.pathname ?? config?.url,
        endpointGroup: getEndpointGroup(normalizedPath),
        status: error.response?.status ?? 0,
        durationMs: duration,
        correlationId: getHeaderValue(
          config?.headers,
          REQUEST_HEADERS.CORRELATION_ID,
        ),
        operationId: getHeaderValue(
          config?.headers,
          REQUEST_HEADERS.REQUEST_ID,
        ),
        queryKeys: summarizeQueryParams(url),
        responseBody: summarizeResponsePayload(error.response?.data),
        error: error instanceof Error ? error : undefined,
      },
      { captureSentryMessage: failureLevel === 'warn' },
    );

    throw error instanceof Error
      ? error
      : new Error((error as { message?: string })?.message ?? String(error));
  },
);

/**
 * Proactively refreshes the Firebase ID token and updates server cookies.
 * This function should be called before starting long-running operations
 * like batch imports to prevent token expiration mid-process.
 *
 * @returns Promise that resolves when token is refreshed
 * @throws Error if user is not authenticated or refresh fails
 */
export const refreshIdToken = async (): Promise<void> => {
  const { currentUser } = auth;

  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  // Force refresh the Firebase ID token
  const newIdToken = await currentUser.getIdToken(true);
  const refreshToken = currentUser.refreshToken ?? EMPTY_STRING;

  // Dynamically import to avoid circular dependency
  const { postIdTokens } = await import('./auth/postIdTokens');

  // Update cookies with new tokens
  await postIdTokens({
    idToken: newIdToken,
    refreshToken,
  });
};

export default apiClient;
